import React, { useContext, useEffect } from 'react';
import { Box } from '@mui/material';
import { AuthContext } from '../contexts/AuthContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Kicker, AmberButton, EngGrid } from '../components/design';

const MONO = "'IBM Plex Mono', monospace";

// ── Step section ─────────────────────────────────────────────────────────────
const StepSection = ({ num, label, title, body, bullets, screenshot, screenshotAlt, url, callout, calloutColor = '#2dd4bf', calloutPos, dark = false, reversed = false }) => {
  const lightFrame = {
    border: '1px solid #d4dbe6',
    borderRadius: '14px',
    overflow: 'hidden',
    boxShadow: '0 24px 60px rgba(20,40,80,.14)',
    background: '#fff',
  };
  const darkFrame = {
    border: '1px solid rgba(120,150,200,.28)',
    borderRadius: '14px',
    overflow: 'hidden',
    boxShadow: '0 30px 70px rgba(0,0,0,.5)',
    background: '#0c1830',
  };

  return (
    <Box
      component="section"
      sx={{
        position: 'relative',
        background: dark ? '#0a1326' : '#eef1f6',
        color: dark ? '#f4f8ff' : '#0e1726',
        py: '88px',
        px: '28px',
        overflow: 'hidden',
      }}
    >
      {dark && <EngGrid opacity={0.4} animate={false} />}
      <Box
        sx={{
          position: 'relative',
          maxWidth: '1160px',
          mx: 'auto',
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: '50px',
          alignItems: 'center',
        }}
      >
        {/* Text block */}
        <Box sx={{ order: { xs: 1, md: reversed ? 2 : 1 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '14px', mb: '18px' }}>
            <Box sx={{ fontFamily: MONO, fontSize: '13px', fontWeight: 600, color: dark ? '#2dd4bf' : '#0d47a1', letterSpacing: '.1em' }}>{num}</Box>
            <Box sx={{ height: '1px', width: '46px', background: dark ? 'rgba(120,150,200,.4)' : '#c2cbda' }} />
            <Box sx={{ fontFamily: MONO, fontSize: '12px', letterSpacing: '.14em', textTransform: 'uppercase', color: dark ? '#8298bd' : '#7a89a3' }}>{label}</Box>
          </Box>
          <Box component="h2" sx={{ m: 0, mb: '12px', fontSize: 'clamp(28px,3.4vw,38px)', fontWeight: 700, letterSpacing: '-.02em', color: dark ? '#f4f8ff' : '#0e1726', lineHeight: 1.12 }}>
            {title}
          </Box>
          <Box sx={{ m: 0, mb: '26px', fontSize: '16.5px', lineHeight: 1.65, color: dark ? '#aebfdd' : '#5a6b85', maxWidth: '480px' }}>
            {body}
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '13px' }}>
            {bullets.map(({ icon, text }) => (
              <Box key={text} sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                {icon}
                <Box sx={{ fontSize: '15px', color: dark ? '#dce6fa' : '#2a3850' }}>{text}</Box>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Media block */}
        <Box sx={{ position: 'relative', order: { xs: 2, md: reversed ? 1 : 2 } }}>
          <Box sx={dark ? darkFrame : lightFrame}>
            {/* Browser chrome */}
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: '7px', px: '14px', py: '11px',
              borderBottom: `1px solid ${dark ? 'rgba(120,150,200,.18)' : '#e6eaf1'}`,
              background: dark ? 'rgba(8,15,30,.7)' : '#f7f9fc',
            }}>
              {['#ff5f57', '#febc2e', '#28c840'].map(c => <Box key={c} sx={{ width: 10, height: 10, borderRadius: '50%', background: c }} />)}
              <Box sx={{ ml: '10px', fontFamily: MONO, fontSize: '11.5px', color: dark ? '#8298bd' : '#8a98b0' }}>{url}</Box>
            </Box>
            <Box component="img" src={screenshot} alt={screenshotAlt} sx={{ display: 'block', width: '100%', height: 'auto', ...(screenshot.includes('export') ? { maxHeight: '420px', objectFit: 'cover', objectPosition: 'top' } : {}) }} />
          </Box>
          {/* Floating callout */}
          <Box sx={{
            position: 'absolute',
            ...calloutPos,
            background: dark ? '#08111f' : '#0a1326',
            border: `1px solid ${calloutColor}`,
            borderRadius: '8px',
            px: '11px', py: '7px',
            fontFamily: MONO, fontSize: '11px', color: calloutColor,
            boxShadow: '0 12px 30px rgba(0,0,0,.3)',
            animation: 'floatY 5s ease-in-out infinite',
            '@keyframes floatY': { '0%,100%': { transform: 'translateY(0)' }, '50%': { transform: 'translateY(-7px)' } },
            '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
          }}>
            {callout}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

// ── Check icon (light) ───────────────────────────────────────────────────────
const BlueCheck = () => (
  <Box sx={{ width: 18, height: 18, borderRadius: '5px', background: '#0d47a1', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', flexShrink: 0 }}>✓</Box>
);

// ── Detection box icon ───────────────────────────────────────────────────────
const DetectBox = ({ style }) => (
  <Box sx={{ width: 18, height: 18, borderRadius: '5px', flexShrink: 0, ...style }} />
);

// ── Main ─────────────────────────────────────────────────────────────────────
const LearnMore = () => {
  const { isAuthenticated } = useContext(AuthContext);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <Box sx={{ background: '#0a1326', overflowX: 'hidden' }}>
      <Navbar />

      {/* ── HEADER ── */}
      <Box
        component="header"
        sx={{
          position: 'relative',
          background: 'radial-gradient(1000px 540px at 50% -10%, rgba(47,109,240,.18), transparent 60%), #0a1326',
          py: '78px',
          px: '28px',
          textAlign: 'center',
          overflow: 'hidden',
        }}
      >
        <EngGrid opacity={0.5} />
        <Box sx={{ position: 'relative', maxWidth: '820px', mx: 'auto' }}>
          <Kicker variant="pill" sx={{ display: 'inline-flex', mb: '24px' }}>How it works</Kicker>
          <Box
            component="h1"
            sx={{
              m: 0, mb: '18px',
              fontSize: 'clamp(38px,5vw,58px)',
              lineHeight: 1.05,
              fontWeight: 700,
              letterSpacing: '-.02em',
              color: '#f4f8ff',
            }}
          >
            From a flat drawing to<br />structured plant data.
          </Box>
          <Box sx={{ m: 0, mx: 'auto', maxWidth: '560px', fontSize: '18px', lineHeight: 1.6, color: '#aebfdd' }}>
            PANDID turns your P&IDs into validated, queryable data in three steps — upload, detect & verify, export. Here's exactly how each stage works.
          </Box>
        </Box>

        {/* Process rail */}
        <Box sx={{ position: 'relative', maxWidth: '880px', mx: 'auto', mt: '48px' }}>
          {/* Amber dashed marching line */}
          <Box sx={{
            display: { xs: 'none', md: 'block' },
            position: 'absolute',
            top: '23px',
            left: '14%', right: '14%',
            height: '2px',
            backgroundImage: 'repeating-linear-gradient(90deg,#f5a524 0 7px,transparent 7px 17px)',
            backgroundSize: '200px 2px',
            animation: 'flowX 5s linear infinite',
            '@keyframes flowX': { to: { backgroundPosition: '200px 0' } },
            opacity: .7,
            '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
          }} />
          <Box sx={{ position: 'relative', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '18px', flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
            {[
              { n: '01', label: 'Upload' },
              { n: '02', label: 'Detect & verify' },
              { n: '03', label: 'Export' },
            ].map(({ n, label }) => (
              <Box key={n} sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', minWidth: '80px' }}>
                <Box sx={{ width: 46, height: 46, borderRadius: '12px', background: '#0a1326', border: '1.5px solid #2dd4bf', color: '#2dd4bf', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: MONO, fontWeight: 600, fontSize: '15px' }}>
                  {n}
                </Box>
                <Box sx={{ fontFamily: MONO, fontSize: '13px', letterSpacing: '.06em', color: '#dce6fa' }}>{label}</Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* ── STEP 01 — UPLOAD (light) ── */}
      <StepSection
        num="§01"
        label="Upload"
        title="Drop a drawing. That's the whole step."
        body="Drag a P&ID straight onto the page or browse to it. Single sheet or a full drawing set — PANDID takes it from there."
        bullets={[
          { icon: <BlueCheck />, text: 'Drag & drop or click to browse' },
          { icon: <BlueCheck />, text: <Box component="span"><Box component="span" sx={{ fontFamily: MONO, fontSize: '13px', color: '#0d47a1' }}>PDF · PNG · JPG · TIFF</Box>, up to 10MB</Box> },
          { icon: <BlueCheck />, text: 'Batch upload across a drawing set' },
          { icon: <BlueCheck />, text: 'Secure, private file handling' },
        ]}
        screenshot="/images/screenshot-upload-2.png"
        screenshotAlt="PANDID upload screen — drag and drop a P&ID file"
        url="pandid.io/upload"
        callout="multi-format · ≤10MB"
        calloutPos={{ bottom: '-14px', right: '18px' }}
        dark={false}
      />

      {/* ── STEP 02 — DETECT & VERIFY (dark) ── */}
      <StepSection
        num="§02"
        label="Detect & verify"
        title="The AI reads it. You sign off on it."
        body="PANDID frames every element, instrument and line with a tagged bounding box, then maps the connections between them. You review the overlay on the original drawing and confirm or correct — element by element."
        bullets={[
          { icon: <DetectBox style={{ border: '2px solid #2dd4bf' }} />, text: 'Automatic element & connection detection' },
          { icon: <DetectBox style={{ border: '2px solid #44d07b' }} />, text: 'Confirm in place — green when verified' },
          { icon: <DetectBox style={{ border: '2px dashed #f5a524' }} />, text: 'Real-time corrections on the live overlay' },
          { icon: <DetectBox style={{ border: '2px solid #2dd4bf' }} />, text: 'Always pinned to the source drawing' },
        ]}
        screenshot="/images/screenshot-drawing-view-2.png"
        screenshotAlt="PANDID verification view — detected elements and connections overlaid on a P&ID"
        url="pandid.io/drawing/p42-1010"
        callout="element confirmed ✓"
        calloutColor="#44d07b"
        calloutPos={{ top: '26%', left: '-12px' }}
        dark
        reversed
      />

      {/* ── STEP 03 — EXPORT (light) ── */}
      <StepSection
        num="§03"
        label="Export"
        title="Structured data, not a screenshot."
        body={
          <Box component="span">
            Download a clean Excel register of every element, plus the connections as{' '}
            {['Source', 'Relationship', 'Target'].map((w, i) => (
              <Box key={w} component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                {i > 0 && <Box component="span" sx={{ color: '#b0bbd0', mx: '2px' }}>→</Box>}
                <Box component="span" sx={{ fontFamily: MONO, fontSize: '11px', color: '#0d47a1', background: 'rgba(13,71,161,.08)', border: '1px solid rgba(13,71,161,.22)', borderRadius: '5px', px: '7px', py: '2px', letterSpacing: '.03em', whiteSpace: 'nowrap' }}>{w}</Box>
              </Box>
            ))}
            {' '}edges — ready for your workflows today and graph databases next.
          </Box>
        }
        bullets={[
          { icon: <BlueCheck />, text: <Box component="span">.CSV export, ready to use in Excel or databases</Box> },
          { icon: <BlueCheck />, text: 'Process flow and process control relationships' },
          { icon: <BlueCheck />, text: 'Graph-database ready structure' },
        ]}
        screenshot="/images/screenshot-export-2.png"
        screenshotAlt="PANDID export — Source, Relationship, Target columns in a spreadsheet"
        url="drawing_data — export.csv"
        callout="// graph-ready triples"
        calloutPos={{ bottom: '-14px', left: '18px' }}
        dark={false}
      />

      {/* ── CTA (dark) ── */}
      <Box component="section" sx={{ position: 'relative', background: '#0a1326', py: '94px', px: '28px', overflow: 'hidden' }}>
        <Box sx={{ position: 'absolute', inset: 0, pointerEvents: 'none', background: 'radial-gradient(700px 380px at 50% 0%, rgba(47,109,240,.18), transparent 60%)' }} />
        <Box sx={{ position: 'relative', maxWidth: '720px', mx: 'auto', textAlign: 'center' }}>
          <Kicker variant="plain" sx={{ display: 'inline-block', mb: '18px' }}>ready when you are</Kicker>
          <Box component="h2" sx={{ m: 0, mb: '16px', fontSize: 'clamp(30px,4vw,46px)', fontWeight: 700, letterSpacing: '-.02em', color: '#f4f8ff' }}>
            Transform your first P&ID today.
          </Box>
          <Box sx={{ mx: 'auto', mb: '34px', maxWidth: '500px', fontSize: '17px', lineHeight: 1.6, color: '#aebfdd' }}>
            Unlimited drawings, one annual price. Start extracting structured data from your technical drawings now.
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '12px', justifyContent: 'center' }}>
            <AmberButton to={isAuthenticated ? '/dashboard' : '/register'} size="lg" sx={{ fontSize: '15.5px', py: '14px', px: '28px' }}>
              {isAuthenticated ? 'Go to Dashboard' : 'Get started'}{' '}
              <Box component="span" sx={{ fontFamily: MONO }}>→</Box>
            </AmberButton>
            <Box
              component="a"
              href="/pricing"
              sx={{
                display: 'inline-flex', alignItems: 'center', gap: '9px', textDecoration: 'none',
                background: 'transparent', color: '#dce6fa', fontWeight: 600, fontSize: '15.5px',
                py: '14px', px: '26px', borderRadius: '10px', border: '1px solid rgba(120,150,200,.34)',
                transition: 'border-color .15s, color .15s',
                '&:hover': { borderColor: 'rgba(45,212,191,.6)', color: '#fff' },
              }}
            >
              View pricing
            </Box>
          </Box>
        </Box>
      </Box>

      <Footer />
    </Box>
  );
};

export default LearnMore;
